import { Add } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import {
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import CircularProgressWithLabel from 'common/components/CircularProgressWithLabel';
import CustomAccordion from 'common/components/CustomAccordion';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTextFieldDescription from 'common/components/CustomTextFieldDescription';
import CustomTextValidator from 'common/components/CustomTextValidators';
import CustomTooltip from 'common/components/CustomTooltip';
import ImageFileInput from 'common/components/ImageFileInput';
import LabelsInput from 'common/features/labels/LabelsInput';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import useFlexBoxStyles from 'common/flexBox';
import { useAbortController } from 'common/hooks';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { messageNotAuthorize } from 'common/utils/CONST';
import { handleUploadFileSignedUrl } from 'common/utils/file';
import { RenderDateInput } from 'common/utils/utils';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useSelector } from 'react-redux';
import { getOrganization } from 'redux/slices/organizationSlice';
import EventFormReminders from './EventFormReminders';
import EventFormSettingsChannels from './EventFormSettingsChannels';

const EventFormSettings = (props) => {
    const {
        enableTvWidget,
        channelSubscriptionEnable,
        type,
        enableLabels,
        event,
        handleFieldChange,
        step,
        setStep,
        cancel,
        open,
        close,
        availableChannelsCount,
    } = props;

    const { t } = useTranslation();

    const { classes: flexBox, cx } = useFlexBoxStyles();

    const { enqueueSnackbar } = useSnackbar();
    const organization = useSelector(getOrganization);
    const isAuthorize = useAuthLicenseAddOns();
    const [uploadMainImageProgress, setUploadMainImageProgress] = useState(0);
    const [uploadingMainImage, setUploadingMainImage] = useState(false);
    const isAuthorizeFullLookAndFeel = isAuthorize(['full_look_and_feel']);
    const [limitConcurrentUsers, setLimitConcurrentUsers] = useState(event.limitConcurrentUsers > 0);
    const getSignal = useAbortController();
    const enableTvChannelManager = useSettingValue(SettingsKey.ENABLE_TV_CHANNEL_MANAGER);

    const channelSelectionRequired = () => {
        return (
            event &&
            enableTvChannelManager &&
            event?.isPublic &&
            typeof availableChannelsCount === 'number' &&
            availableChannelsCount > 1
            // && Array.isArray(event?.channelsIds)
            // && event.channelsIds.length === 0
        );
    };

    const validChannelSelection = () => {
        if (channelSelectionRequired()) {
            return event.channelsIds?.length > 0;
        } else {
            return true;
        }
    };

    const validateForm = () => {
        return (
            event.name?.length < 100 &&
            (type === 'one_to_one' || (type === 'one_to_many' && event.limitConcurrentUsers >= 0)) &&
            validChannelSelection()
        );
    };

    useEffect(() => {
        if (event.hostProvider !== 'web') {
            handleFieldChange({
                target: {
                    name: 'isAdminHost',
                    value: false,
                },
            });
        }
    }, [event.hostProvider, handleFieldChange]);

    const handleReleaseDateChange = (e) => {
        const releaseDate = new Date(e);
        if (releaseDate >= new Date()) {
            handleFieldChange({
                target: {
                    name: 'releaseDate',
                    value: e,
                },
            });
        }
    };

    const submit = () => {
        setStep(step + 1);
    };

    const handleChangeUnlimited = (e) => {
        setLimitConcurrentUsers(e.target.checked);
        if (!e.target.checked) {
            handleFieldChange({
                target: {
                    name: 'limitConcurrentUsers',
                    value: 0,
                },
            });
        }
    };

    const handleSkipLeadCreationChange = (e) => {
        handleFieldChange({
            target: {
                name: 'skipLeadCreation',
                value: e.target.checked,
            },
        });
    };

    const handleOBSHostProviderChange = (e) => {
        handleFieldChange({
            target: {
                name: 'hostProvider',
                value: e.target.checked ? 'obs' : 'web',
            },
        });
    };

    const updateReminders = (reminders) => {
        handleFieldChange({
            target: {
                name: 'reminders',
                value: reminders,
            },
        });
    };

    const customizeReminders = () => {
        open(<EventFormReminders close={close} eventReminders={event.reminders} onConfirm={updateReminders} />);
    };

    const scheduledSwitch = (
        <FormGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        className={cx(flexBox.padding0, flexBox.margin01)}
                        checked={event.instant}
                        onChange={(e) => handleFieldChange(e, 'instant')}
                        name="instant"
                        color="primary"
                    />
                }
                label={<FormLabel>{t('event.eventFormSettings.enableInstantEvent')}</FormLabel>}
            />
        </FormGroup>
    );

    const obsProviderSwitch = (
        <FormGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        className={cx(flexBox.padding0, flexBox.margin01)}
                        checked={event.hostProvider === 'obs'}
                        onChange={(e) => handleOBSHostProviderChange(e)}
                        name="hostProvider"
                        disabled={!isAuthorize(['professional_pack'])}
                        color="primary"
                    />
                }
                label={<FormLabel>{t('event.eventFormSettings.enableOBSHostProvider')}</FormLabel>}
            />
        </FormGroup>
    );

    const uploadImageFile = (file, setProgress, setUploading, callback) => {
        handleUploadFileSignedUrl({
            file,
            size: 1024 * 1024 * 100, // 100MB
            onInit: (size) => {
                setUploading(true);
                setProgress(0);
            },
            onProgress: (loaded, total) => {
                setProgress(Math.min(100, Math.round((loaded / total) * 100)));
            },
            onComplete: (url) => {
                setUploading(false);
                callback(url);
            },
            onError: (error) => {
                setUploading(false);
                if (error.code !== 'ERR_CANCELED') {
                    enqueueSnackbar(t('app.common.uploadError'), {
                        variant: 'error',
                    });
                }
            },
            bucketType: 'resources',
            signal: getSignal(),
        });
    };

    const handleMainImageUploadFile = (imgFile) => {
        if (imgFile.size / 1000 > 500) {
            enqueueSnackbar(t('upload.imageSizeError', { size: '500kb' }), {
                variant: 'error',
            });
        } else {
            uploadImageFile(imgFile, setUploadMainImageProgress, setUploadingMainImage, (imageUrl) => {
                handleFieldChange({
                    target: {
                        name: 'mainImage',
                        value: imageUrl,
                    },
                });
            });
        }
    };

    const renderButtonMainImage = ({ disabled }) => (
        <CustomIconButton size="small" disabled={disabled} component="span">
            {uploadingMainImage && <CircularProgressWithLabel value={uploadMainImageProgress} />}
            {!uploadingMainImage && <Add />}
        </CustomIconButton>
    );

    const canBeInstantEvent = () => {
        const release = new Date(event.releaseDate).setHours(0, 0, 0, 0);
        const actual = new Date().setHours(0, 0, 0, 0);
        return release === actual;
    };

    const handleTagChange = (e) => {
        handleFieldChange({
            target: {
                name: 'labels',
                value: e,
            },
        });
    };

    const handleError = () => {
        enqueueSnackbar(t('account.accountProfileForm.formError'), {
            variant: 'error',
        });
    };

    return (
        <ValidatorForm
            onSubmit={submit}
            onError={handleError}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
        >
            <Stack
                direction="column"
                spacing={3}
                className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
            >
                <FormGroup>
                    <FormLabel>{t('event.eventFormSettings.title')}</FormLabel>
                    <CustomTextValidator
                        name="name"
                        fullWidth={true}
                        variant="outlined"
                        size="small"
                        autoComplete="new-name"
                        value={event.name}
                        onChange={handleFieldChange}
                        validators={['maxStringLength: 100']}
                        errorMessages={[t('event.form.maxLength')]}
                    />
                </FormGroup>

                {canBeInstantEvent() && <FormGroup>{scheduledSwitch}</FormGroup>}

                {!event.instant && (
                    <FormGroup>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <FormLabel>{t('event.eventFormSettings.releaseDate')}</FormLabel>
                            <Link component="button" onClick={customizeReminders}>
                                <Typography>{t('event.eventFormSettings.customizeAction')}</Typography>
                            </Link>
                        </Stack>
                        <FormControl>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    autoOk
                                    variant="inline"
                                    value={event.releaseDate}
                                    size="small"
                                    inputVariant="outlined"
                                    onChange={handleReleaseDateChange}
                                    minDate={new Date()}
                                    inputFormat="dd/MM/yyyy hh:mm a"
                                    renderInput={(params) => <RenderDateInput {...params} />}
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </FormGroup>
                )}
                <FormGroup>
                    <FormLabel>{t('event.eventFormSettings.description')}</FormLabel>
                    <FormControl variant="outlined" size="small">
                        <CustomTextFieldDescription
                            name="description"
                            value={event.description}
                            onChange={handleFieldChange}
                            variant="outlined"
                            size="small"
                            multiline
                            rows={3}
                            placeholder={t('event.eventFormSettings.descriptionPlaceholder')}
                        />
                    </FormControl>
                </FormGroup>

                <CustomAccordion disableGutters className={flexBox.marginTop2}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content">
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className={flexBox.w100}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FormLabel>{t('profile.organization.coverImage')}</FormLabel>

                            <Stack direction="row" spacing={1}>
                                {event?.mainImage && (
                                    <CustomTooltip
                                        arrow
                                        title={t('event.eventFormSettings.setOrganizationMainImage')}
                                        placement={'bottom'}
                                    >
                                        <CustomIconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFieldChange({
                                                    target: {
                                                        name: 'mainImage',
                                                        value: null,
                                                    },
                                                });
                                            }}
                                        >
                                            <WallpaperIcon component="svg" />
                                        </CustomIconButton>
                                    </CustomTooltip>
                                )}

                                <ImageFileInput
                                    inputId={'raised-button-main-image-file'}
                                    uploadAssetFn={handleMainImageUploadFile}
                                    disabled={uploadingMainImage || !isAuthorizeFullLookAndFeel}
                                />

                                <label htmlFor="raised-button-main-image-file" className={flexBox.paddingRight1}>
                                    {isAuthorizeFullLookAndFeel ? (
                                        <CustomTooltip
                                            arrow
                                            title={t('profile.organization.upload')}
                                            placement={'bottom'}
                                        >
                                            {renderButtonMainImage({ disabled: false })}
                                        </CustomTooltip>
                                    ) : (
                                        <CustomTooltip arrow title={t(messageNotAuthorize)} placement={'bottom'}>
                                            {renderButtonMainImage({ disabled: true })}
                                        </CustomTooltip>
                                    )}
                                </label>
                            </Stack>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            className={flexBox.w100}
                        >
                            {event?.mainImage && (
                                <img
                                    src={event.mainImage}
                                    className={cx(flexBox.w50, flexBox.hAuto, flexBox.margin20)}
                                    alt="main_event"
                                />
                            )}

                            {!event?.mainImage && organization?.mainImage && (
                                <img
                                    src={organization.mainImage}
                                    className={cx(flexBox.w50, flexBox.hAuto, flexBox.margin20)}
                                    alt="main_event"
                                />
                            )}
                        </Stack>
                    </AccordionDetails>
                </CustomAccordion>

                {enableTvWidget && type === 'one_to_many' && (
                    <EventFormSettingsChannels
                        channelSubscriptionEnable={channelSubscriptionEnable}
                        event={event}
                        handleFieldChange={handleFieldChange}
                        channelSelectionRequired={channelSelectionRequired()}
                    />
                )}

                {type === 'one_to_many' &&
                    (!isAuthorize(['professional_pack']) ? (
                        <CustomTooltip arrow title={t(messageNotAuthorize)} placement={'left'}>
                            {obsProviderSwitch}
                        </CustomTooltip>
                    ) : (
                        obsProviderSwitch
                    ))}

                {type === 'one_to_many' && (
                    <>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        className={cx(flexBox.padding0, flexBox.margin01)}
                                        checked={limitConcurrentUsers}
                                        onChange={(e) => handleChangeUnlimited(e)}
                                        name="limit"
                                        color="primary"
                                    />
                                }
                                label={
                                    <FormLabel>{t('event.eventFormSettings.enableLimitForConcurrentUser')}</FormLabel>
                                }
                            />
                        </FormGroup>

                        {limitConcurrentUsers && (
                            <FormGroup>
                                <FormLabel>{t('event.eventFormSettings.limitConcurrentUsers')}</FormLabel>
                                <FormControl variant="outlined" size="small">
                                    <CustomTextValidator
                                        name="limitConcurrentUsers"
                                        value={event.limitConcurrentUsers}
                                        onChange={handleFieldChange}
                                        variant="outlined"
                                        size="small"
                                        type="number"
                                        validators={['minNumber:0']}
                                        errorMessages={[t('event.eventFormSettings.errorLimitConcurrentUsers')]}
                                        fullWidth
                                        placeholder={t('event.eventFormSettings.limitConcurrentUsersPlaceholder')}
                                    />
                                </FormControl>
                            </FormGroup>
                        )}
                    </>
                )}

                {event.isPublic && type === 'one_to_many' && enableLabels && (
                    <FormGroup>
                        <FormLabel>{t('eventform.tags')}</FormLabel>
                        <FormControl>
                            <LabelsInput
                                deleteAction={true}
                                namespace={'event'}
                                bannedChars={true}
                                labels={event.labels}
                                onChange={(e) => handleTagChange(e)}
                                placeholder={t('labels.placeholder')}
                            />
                        </FormControl>
                    </FormGroup>
                )}

                {type === 'one_to_many' && isAuthorize(['leads']) ? (
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    className={cx(flexBox.padding0, flexBox.margin01)}
                                    checked={event.skipLeadCreation}
                                    onChange={handleSkipLeadCreationChange}
                                    name="skipLeadCreation"
                                    color="primary"
                                />
                            }
                            label={<FormLabel>{t('event.eventFormSettings.skipLeadCreation')}</FormLabel>}
                        />
                    </FormGroup>
                ) : null}
            </Stack>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton color="primary" autoFocus fitWidth disabled={!validateForm()} type="submit">
                    {t('event.eventFormSettings.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default EventFormSettings;
